import { useEffect, useRef } from 'react';
import { useGetFeatureFlagExperiment } from 'js/sign-components/common/feature-flag';
import { useLogPAPEvent } from 'hellospa/common/use-log-pap-event';
import { PAP_Exposure_SignGrowthbook } from '@dropbox/pap-events/sign/exposure_sign_growthbook';

const EXPERIMENT_NAME =
  'sign_core_2026_01_19_restrict_cancel_now_button_to_california_users';
const STORAGE_KEY = `exposure_${EXPERIMENT_NAME}`;

/**
 * Custom hook for the restrict cancel now button experiment.
 * Handles feature flag evaluation and automatic exposure tracking with deduplication.
 *
 * @param isCaliforniaUser - Whether the current user is located in California
 * @returns Object containing experiment state and configuration
 */
export function useRestrictCancelNowExperiment(isCaliforniaUser: boolean) {
  const logPAPEvent = useLogPAPEvent();
  const hasLoggedExposure = useRef(false);

  const experiment = useGetFeatureFlagExperiment(EXPERIMENT_NAME);

  const isEnabled =
    experiment && typeof experiment === 'object' && 'status' in experiment
      ? experiment.status
      : !!experiment;

  const restrictCancelNow = isEnabled && !isCaliforniaUser;

  useEffect(() => {
    // Check if we've already logged exposure in this session
    const hasExposureInSession = sessionStorage.getItem(STORAGE_KEY);

    if (
      typeof experiment === 'object' &&
      experiment.inExperiment &&
      !isCaliforniaUser &&
      !hasLoggedExposure.current &&
      !hasExposureInSession
    ) {
      // Log exposure event once per session
      logPAPEvent(
        PAP_Exposure_SignGrowthbook({
          experimentVariant: (experiment.variationId ?? 0).toString(),
          feature: EXPERIMENT_NAME,
        }),
      );

      hasLoggedExposure.current = true;
      sessionStorage.setItem(STORAGE_KEY, 'true');
    }
  }, [experiment, isCaliforniaUser, logPAPEvent]);

  return {
    restrictCancelNow,
    isEnabled,
    experiment: typeof experiment === 'object' ? experiment : null,
  };
}
